import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getTaskReferenceFromHref, getTicketPublicIdFromHref } from '../../../utilities/ticketLinks';
import TicketProfileTooltip from './TicketProfileTooltip';
import TaskProfileTooltip from './TaskProfileTooltip';

interface TicketLinkPreviewProps {
    children: React.ReactNode;
    className?: string;
    theme?: 'light' | 'dark';
    active?: boolean;
}

interface ActiveReferenceLink {
    anchor: HTMLAnchorElement;
    type: 'ticket' | 'task';
    reference: string;
}

const TicketLinkPreview: React.FC<TicketLinkPreviewProps> = ({ children, className = '', theme, active = true }) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hideTimerRef = useRef<number>();
    const showTimerRef = useRef<number>();
    const [activeLink, setActiveLink] = useState<ActiveReferenceLink | null>(null);
    const [position, setPosition] = useState({ top: 0, left: 0, ready: false });
    const resolvedTheme = theme ?? (document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    const visibility = useRef({ active, generation: 0 });
    if (visibility.current.active !== active) visibility.current = { active, generation: visibility.current.generation + 1 };

    useLayoutEffect(() => {
        visibility.current.active = active;
        if (!active) setActiveLink(null);
        window.clearTimeout(showTimerRef.current);
        window.clearTimeout(hideTimerRef.current);
        return () => {
            visibility.current.active = false;
            visibility.current.generation++;
            window.clearTimeout(showTimerRef.current);
            window.clearTimeout(hideTimerRef.current);
        };
    }, [active]);

    const showForTarget = useCallback((target: EventTarget | null, immediate = false) => {
        if (!visibility.current.active) return;
        if (!(target instanceof Element)) return;
        const anchor = target.closest('a');
        if (!(anchor instanceof HTMLAnchorElement)) return;
        const href = anchor.getAttribute('href') ?? '';
        const ticketReference = getTicketPublicIdFromHref(href);
        const taskReference = getTaskReferenceFromHref(href);
        if (!ticketReference && !taskReference) return;
        window.clearTimeout(hideTimerRef.current);
        window.clearTimeout(showTimerRef.current);
        const generation = visibility.current.generation;
        const show = () => {
            if (!visibility.current.active || visibility.current.generation !== generation || !anchor.isConnected) return;
            setPosition(previous => ({ ...previous, ready: false }));
            setActiveLink({
                anchor,
                type: ticketReference ? 'ticket' : 'task',
                reference: ticketReference ?? taskReference!,
            });
        };
        if (immediate) show();
        else showTimerRef.current = window.setTimeout(show, 350);
    }, []);

    const hide = useCallback((delay = 80) => {
        window.clearTimeout(hideTimerRef.current);
        window.clearTimeout(showTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => setActiveLink(null), delay);
    }, []);

    useLayoutEffect(() => {
        if (!active || !activeLink) return;

        const updatePosition = () => {
            const anchorRect = activeLink.anchor.getBoundingClientRect();
            const tooltipRect = tooltipRef.current?.getBoundingClientRect();
            if (!tooltipRect) return;
            const padding = 12;
            const gap = 8;
            let left = anchorRect.left + (anchorRect.width - tooltipRect.width) / 2;
            left = Math.min(Math.max(padding, left), window.innerWidth - tooltipRect.width - padding);
            const topAbove = anchorRect.top - tooltipRect.height - gap;
            const top = topAbove >= padding ? topAbove : anchorRect.bottom + gap;
            setPosition({ top, left, ready: true });
        };

        const resizeObserver = typeof ResizeObserver !== 'undefined' && tooltipRef.current
            ? new ResizeObserver(updatePosition)
            : null;
        resizeObserver?.observe(tooltipRef.current!);
        const frame = window.requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.cancelAnimationFrame(frame);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [active, activeLink]);

    return (
        <div
            className={`contents ${className}`}
            onMouseOver={event => showForTarget(event.target)}
            onMouseOut={event => {
                const anchor = event.target instanceof Element ? event.target.closest('a') : null;
                if (anchor && event.relatedTarget instanceof Node && anchor.contains(event.relatedTarget)) return;
                hide();
            }}
            onFocus={event => showForTarget(event.target, true)}
            onBlur={() => hide(0)}
            onKeyDown={event => {
                if (event.key === 'Escape') hide(0);
            }}
        >
            {children}
            {active && activeLink && createPortal(
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    className={`fixed z-[999999999] rounded-xl p-3 transition-opacity duration-150 pointer-events-none ${resolvedTheme === 'dark' ? 'border-0 bg-[#23252f] shadow-[0_18px_48px_rgba(0,0,0,0.42)]' : 'border border-slate-200/70 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.16)]'} ${position.ready ? 'opacity-100' : 'opacity-0'}`}
                    style={{ top: position.top, left: position.left }}
                >
                    {activeLink.type === 'ticket'
                        ? <TicketProfileTooltip publicId={activeLink.reference} theme={resolvedTheme} />
                        : <TaskProfileTooltip taskReference={activeLink.reference} theme={resolvedTheme} />}
                </div>,
                document.body,
            )}
        </div>
    );
};

export default TicketLinkPreview;
